import React, { useState, useEffect } from "react";
import styles from "./profile.module.css";
import { useEditProfile, useProfile } from "../../hooks/useProfile";
import PageInputField from "../../components/ui/input/PagesInput/PagesInput";
import Button from "../../components/ui/buttons/Button";
import Loader from "../../components/ui/Loader/Loader";
import mainStyle from "../../style/Main.module.css";

import {
  FaMapMarkerAlt,
  FaPhone,
  FaUser,
  FaCalendarAlt,
  FaGlobe,
  FaEnvelope,
} from "react-icons/fa";
import { MdEdit, MdCancel } from "react-icons/md";
import { TfiClose } from "react-icons/tfi";

const ProfileSkeleton = () => {
  return (
    <div className={styles.popupBackground}>
      <div className={styles.profileMainContainer}>
        <div className={styles.profileContainer}>
          <div className={styles.header}>
            <div className={styles.headerContent}>
              <div
                className={`${styles.skeleton} ${styles.skeletonTitle}`}
              ></div>
              <div
                className={`${styles.skeleton} ${styles.skeletonSubtitle}`}
              ></div>
            </div>
            <div
              className={`${styles.skeleton} ${styles.skeletonButton}`}
            ></div>
            <div className={`${styles.skeleton} ${styles.skeletonClose}`}></div>
          </div>

          <div className={styles.profileCard}>
            <div className={styles.profileHeader}>
              <div className={styles.avatarContainer}>
                <div className={styles.avatarWrapper}>
                  <div
                    className={`${styles.skeleton} ${styles.skeletonAvatar}`}
                  ></div>
                </div>
              </div>

              <div className={styles.profileInfo}>
                <div className={styles.nameFields}>
                  <div
                    className={`${styles.skeleton} ${styles.skeletonName}`}
                  ></div>
                </div>

                <div className={styles.metaInfo}>
                  <div
                    className={`${styles.skeleton} ${styles.skeletonMeta}`}
                  ></div>
                  <div
                    className={`${styles.skeleton} ${styles.skeletonMeta}`}
                  ></div>
                </div>
              </div>
            </div>

            <div className={styles.profileDetails}>
              <div className={styles.detailSection}>
                <div className={styles.sectionHeader}>
                  <div
                    className={`${styles.skeleton} ${styles.skeletonIcon}`}
                  ></div>
                  <div
                    className={`${styles.skeleton} ${styles.skeletonSectionTitle}`}
                  ></div>
                </div>

                <div className={styles.detailItem}>
                  <div
                    className={`${styles.skeleton} ${styles.skeletonLabel}`}
                  ></div>
                  <div
                    className={`${styles.skeleton} ${styles.skeletonValue}`}
                  ></div>
                </div>

                <div className={styles.detailItem}>
                  <div
                    className={`${styles.skeleton} ${styles.skeletonLabel}`}
                  ></div>
                  <div
                    className={`${styles.skeleton} ${styles.skeletonValue}`}
                  ></div>
                </div>

                <div className={styles.detailItem}>
                  <div
                    className={`${styles.skeleton} ${styles.skeletonLabel}`}
                  ></div>
                  <div
                    className={`${styles.skeleton} ${styles.skeletonValue}`}
                  ></div>
                </div>
              </div>

              <div className={styles.detailSection}>
                <div className={styles.sectionHeader}>
                  <div
                    className={`${styles.skeleton} ${styles.skeletonIcon}`}
                  ></div>
                  <div
                    className={`${styles.skeleton} ${styles.skeletonSectionTitle}`}
                  ></div>
                </div>
                <div
                  className={`${styles.skeleton} ${styles.skeletonAbout}`}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Profile = ({ onClose, upDated }) => {
  const { loading: isProfileLoading, profileDate } = useProfile();
  const [timeZoneString, setTimeZoneString] = useState("");

  const [isEditing, setIsEditing] = useState(false);
  const apiUrl = process.env.REACT_APP_API_URL;
  const {
    isLoading,
    profile,
    setProfile,
    handleImageChange,
    handleInputChange,
    previewImage,
    handleSubmit,
    error,
  } = useEditProfile(profileDate?.id);

  useEffect(() => {
    if (profileDate) {
      setProfile({
        first_name: profileDate.first_name || "",
        last_name: profileDate.last_name || "",
        emp_pic: profileDate.emp_pic || null,
        phone: profileDate.phone || "",
        email: profileDate.email || "",
        onlineStatus: profileDate.online_status || false,
        createdDate: profileDate.join_date || "",
        about: profileDate.about || "",
        address: profileDate.address || "",
        website: profileDate.website || "",
      });
    }
  }, [profileDate, setProfile]);

  const toggleEdit = () => {
    setIsEditing((prev) => !prev);
    if (isEditing && profileDate) {
      setProfile({
        first_name: profileDate.first_name || "",
        last_name: profileDate.last_name || "",
        emp_pic: profileDate.emp_pic || null,
        phone: profileDate.phone || "",
        email: profileDate.email || "",
        onlineStatus: profileDate.online_status || false,
        createdDate: profileDate.join_date || "",
        about: profileDate.about || "",
        address: profileDate.address || "",
        website: profileDate.website || "",
      });
    }
  };

  useEffect(() => {
    const offset = -new Date().getTimezoneOffset() / 60;
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const gmt = `GMT${offset >= 0 ? "+" : ""}${offset}`;
    setTimeZoneString(`${gmt} (${timeZone.replaceAll("_", " ")})`);
  }, []);

  const handleUpdateProfile = async () => {
    const success = await handleSubmit();
    if (success) {
      onClose();
      upDated(); // ✅ close popup after update success
    }
  };
  if (isProfileLoading) {
    return <ProfileSkeleton />;
  }

  return (
    <div className={styles.popupBackground}>
      <div className={styles.profileMainContainer}>
        <div className={styles.profileContainer}>
          <div className={styles.header}>
            <div className={styles.headerContent}>
              <h1>Profile</h1>
              <p>Manage your personal information and settings</p>
            </div>
            <button
              className={`${styles.editButton} ${
                isEditing ? styles.cancelButton : ""
              }`}
              onClick={toggleEdit}
            >
              {isEditing ? (
                <>
                  <MdCancel className={styles.editIcon} /> Cancel
                </>
              ) : (
                <>
                  <MdEdit className={styles.editIcon} /> Edit
                </>
              )}
            </button>
            <TfiClose size={20} style={{ zIndex: "99999" }} onClick={onClose} />
          </div>

          <div className={styles.profileCard}>
            <div className={styles.profileHeader}>
              <div className={styles.avatarContainer}>
                <div className={styles.avatarWrapper}>
                  {error.emp_pic && (
                    <span className={mainStyle.errorMsg}>{error.emp_pic}</span>
                  )}
                  <div className={styles.avatarFrame}>
                    {previewImage ? (
                      <img
                        src={previewImage}
                        alt="Profile"
                        className={styles.avatar}
                      />
                    ) : profile?.emp_pic ? (
                      <img
                        src={`${apiUrl}${profile.emp_pic}`}
                        alt="Profile"
                        className={styles.avatar}
                      />
                    ) : (
                      <div className={styles.avatarPlaceholder}>
                        {profile?.first_name?.charAt(0)}
                        {profile?.last_name?.charAt(0)}
                      </div>
                    )}
                    {isEditing && (
                      <label className={styles.uploadButton}>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageChange}
                          className={styles.fileInput}
                        />
                        Change
                      </label>
                    )}
                  </div>

                  {/* <div className={styles.statusContainer}>
                    <div
                      className={`${styles.statusIndicator} ${
                        profile?.onlineStatus ? styles.online : styles.offline
                      }`}
                    ></div>
                    <span>{profile?.onlineStatus ? "Online" : "Offline"}</span>
                  </div> */}
                </div>
              </div>

              <div className={styles.profileInfo}>
                <div className={styles.nameFields}>
                  {isEditing ? (
                    <div className={styles.nameInputs}>
                      <PageInputField
                        name="first_name"
                        placeholder="First Name"
                        required
                        value={profile.first_name}
                        onChange={handleInputChange}
                        error={error.first_name}
                        icon={<FaUser />}
                      />
                      <PageInputField
                        name="last_name"
                        placeholder="Last Name"
                        required
                        value={profile.last_name}
                        onChange={handleInputChange}
                        error={error.last_name}
                        icon={<FaUser />}
                      />
                    </div>
                  ) : (
                    <>
                      <h2>
                        {profile?.first_name} {profile?.last_name}
                      </h2>
                    </>
                  )}
                </div>
                {!isEditing && (
                  <div className={styles.metaInfo}>
                    <div className={styles.metaItem}>
                      <FaCalendarAlt className={styles.metaIcon} />
                      <div>
                        <span className={styles.metaLabel}>Member Since</span>
                        <span>{profile?.createdDate}</span>
                      </div>
                    </div>
                    <div className={styles.metaItem}>
                      <FaGlobe className={styles.metaIcon} />
                      <div>
                        <span className={styles.metaLabel}>Timezone</span>
                        <span>{timeZoneString}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className={styles.profileDetails}>
              <div className={styles.detailSection}>
                <div className={styles.sectionHeader}>
                  <h3>Personal Information</h3>
                </div>

                <div className={styles.detailItem}>
                  {isEditing ? (
                    <></>
                  ) : (
                    <>
                      <div className={styles.detailLabel}>
                        <FaEnvelope className={styles.icon} /> Email
                      </div>
                      <span>{profile?.email}</span>
                    </>
                  )}
                </div>

                <div className={styles.detailItem}>
                  <div className={styles.detailLabel}>
                    <FaPhone className={styles.icon} /> Phone
                  </div>
                  {isEditing ? (
                    <PageInputField
                      name="phone"
                      placeholder="e.g., 876543210"
                      required
                      value={profile.phone}
                      onChange={handleInputChange}
                      error={error.phone}
                      fullWidth
                    />
                  ) : (
                    <span>{profile?.phone}</span>
                  )}
                </div>

                <div className={styles.detailItem}>
                  <div className={styles.detailLabel}>
                    <FaMapMarkerAlt className={styles.icon} /> Address
                  </div>
                  {isEditing ? (
                    <PageInputField
                      name="address"
                      placeholder="e.g., 123 Main St, City"
                      value={profile.address}
                      onChange={handleInputChange}
                      error={error.address}
                      fullWidth
                    />
                  ) : (
                    <span>{profile?.address || "Not specified"}</span>
                  )}
                </div>
              </div>

              <div className={styles.detailSection}>
                <div className={styles.sectionHeader}>
                  <h3>About Me</h3>
                </div>
                {isEditing ? (
                  <textarea
                    name="about"
                    value={profile.about}
                    onChange={handleInputChange}
                    className={styles.textarea}
                    rows={5}
                    placeholder="Tell us about yourself, your experience, and your interests..."
                  />
                ) : (
                  <p className={styles.aboutText}>
                    {profile?.about ||
                      "No information provided. Click 'Edit Profile' to add information about yourself."}
                  </p>
                )}
              </div>
            </div>

            {isEditing && (
              <div className={styles.actionButtons}>
                <Button onClick={handleUpdateProfile} primary>
                  {isLoading ? <Loader /> : "Update Profile"}
                </Button>
                {/* <Button onClick={toggleEdit} secondary>
                  Cancel
                </Button> */}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
